export const POSTHOG_EVENTS = {
  PAGE_VIEWED: 'page viewed',
  CTA_CLICKED: 'cta clicked',
  SIGNUP_STARTED: 'signup started',
  SIGNUP_PAGE_VIEWED: 'signup page viewed',
  SIGNUP_SUBMITTED: 'signup submitted',
  SIGNUP_VALIDATION_FAILED: 'signup validation failed',
  SIGNUP_FAILED: 'signup failed',
  SIGNUP_ATTRIBUTION_CAPTURED: 'signup attribution captured',
  USER_SIGNED_UP: 'user signed up',
  USER_SIGNED_IN: 'user signed in',
  AUTO_APPLY_ONBOARDING_STARTED: 'auto apply onboarding started',
  AUTO_APPLY_ONBOARDING_STEP_VIEWED: 'auto apply onboarding step viewed',
  AUTO_APPLY_ONBOARDING_STEP_SUBMITTED: 'auto apply onboarding step submitted',
  AUTO_APPLY_ONBOARDING_BLOCKED: 'auto apply onboarding blocked',
  AUTO_APPLY_ONBOARDING_RESUMED: 'auto apply onboarding resumed',
  AUTO_APPLY_ONBOARDING_RESUME_PROCESSED: 'auto apply onboarding resume processed',
  AUTO_APPLY_ONBOARDING_STEP_COMPLETED: 'auto apply onboarding step completed',
  AUTO_APPLY_ONBOARDING_COMPLETED: 'auto apply onboarding completed',
  AUTO_APPLY_PLAN_VIEWED: 'auto apply plan viewed',
  PLAN_SELECTED: 'plan selected',
  CHECKOUT_SESSION_CREATED: 'checkout session created',
  CHECKOUT_COMPLETED: 'checkout completed',
  TRIAL_STARTED: 'trial started',
  SUBSCRIPTION_STARTED: 'subscription started',
  SUBSCRIPTION_PAYMENT_SUCCEEDED: 'subscription payment succeeded',
  SUBSCRIPTION_PAYMENT_FAILED: 'subscription payment failed',
  SUBSCRIPTION_UPGRADED: 'subscription upgraded',
  SUBSCRIPTION_CANCELLED: 'subscription cancelled',
  AUTO_APPLY_ACTIVATED: 'auto apply activated',
  FIRST_APPLICATION_SUBMITTED: 'first application submitted',
  APPLICATION_SUBMITTED: 'application submitted',
  CREDIT_GRANT_ISSUED: 'credit grant issued',
  FRONTEND_EXCEPTION: 'frontend exception',
  API_EXCEPTION: 'api exception',
  STRIPE_WEBHOOK_EXCEPTION: 'stripe webhook exception',
  LLM_GENERATION_COMPLETED: 'llm generation completed',
  LLM_GENERATION_FAILED: 'llm generation failed',
  SURVEY_SUBMITTED: 'survey submitted',
} as const;

export type PostHogEventName = typeof POSTHOG_EVENTS[keyof typeof POSTHOG_EVENTS] | string;

export type AnalyticsProperties = Record<string, unknown>;

const PROPERTY_ALIASES: Record<string, string> = {
  attributionId: 'attribution_id',
  anonymousId: 'anonymous_id',
  autoId: 'auto_id',
  autoDateCreated: 'auto_date_created',
  authFlow: 'auth_flow',
  authProvider: 'auth_provider',
  cognitoUsername: 'cognito_username',
  identityProvider: 'identity_provider',
  jobSeekerId: 'job_seeker_id',
  userId: 'user_id',
  subscriptionId: 'subscription_id',
  stripeSubscriptionId: 'subscription_id',
  stripeCustomerId: 'stripe_customer_id',
  customerId: 'stripe_customer_id',
  stripeSessionId: 'stripe_session_id',
  checkoutSessionId: 'stripe_session_id',
  stripeEventId: 'stripe_event_id',
  invoiceId: 'invoice_id',
  jobId: 'job_id',
  companyId: 'company_id',
  priceId: 'price_id',
  productId: 'product_id',
  planName: 'plan_name',
  billingInterval: 'billing_interval',
  amountCents: 'amount_cents',
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  initialUtmSource: 'initial_utm_source',
  initialUtmMedium: 'initial_utm_medium',
  initialUtmCampaign: 'initial_utm_campaign',
  initialUtmTerm: 'initial_utm_term',
  initialUtmContent: 'initial_utm_content',
  signupMethod: 'signup_method',
  signupSource: 'signup_source',
  triggerSource: 'trigger_source',
  campaignId: 'campaign_id',
  stepId: 'step_id',
  stepName: 'step_name',
  stepIndex: 'step_index',
  stepNumber: 'step_number',
  totalSteps: 'total_steps',
  applicationCount: 'application_count',
  retryCount: 'retry_count',
  latencyMs: 'latency_ms',
  durationMs: 'duration_ms',
  promptTokens: 'prompt_tokens',
  completionTokens: 'completion_tokens',
  totalTokens: 'total_tokens',
  estimatedCostUsd: 'estimated_cost_usd',
  promptVersion: 'prompt_version',
  templateVersion: 'template_version',
  aiModel: 'ai_model',
  featureFlagKey: 'feature_flag_key',
  featureFlagVariant: 'feature_flag_variant',
  surveyId: 'survey_id',
  surveyName: 'survey_name',
  surveyResponse: 'survey_response',
  httpStatus: 'http_status',
  errorName: 'error_name',
  errorCode: 'error_code',
  errorMessage: 'error_message',
  stripeEventType: 'stripe_event_type',
  hasReferral: 'has_referral',
  isTrial: 'is_trial',
  grantTrial: 'grant_trial',
  isQuarterly: 'is_quarterly',
  paymentFailed: 'payment_failed',
  hasResume: 'has_resume',
  oppref: 'oppref',
  routePattern: 'route_pattern',
  seoPageType: 'seo_page_type',
  competitorSlug: 'competitor_slug',
  ctaPlacement: 'cta_placement',
  landingPage: 'landing_page',
  referringDomain: 'referring_domain',
  initialReferringDomain: 'initial_referring_domain',
  acquisitionChannel: 'acquisition_channel',
  initialAcquisitionChannel: 'initial_acquisition_channel',
};

const ALLOWED_PROPERTIES = new Set([
  '$current_url',
  '$insert_id',
  '$referrer',
  '$set',
  '$set_once',
  'action',
  'amount',
  'amount_cents',
  'anonymous_id',
  'api_route',
  'application_count',
  'acquisition_channel',
  'attribution_id',
  'auth_flow',
  'auth_provider',
  'auto_date_created',
  'auto_id',
  'billing_interval',
  'campaign_id',
  'cancel_at_period_end',
  'cancelled',
  'component',
  'competitor_slug',
  'company_id',
  'completion_tokens',
  'cognito_username',
  'credits',
  'cta_placement',
  'currency',
  'destination',
  'duration_ms',
  'error_code',
  'error_message',
  'error_name',
  'estimated_cost_usd',
  'event_source',
  'feature_flag_key',
  'feature_flag_variant',
  'fbclid',
  'gclid',
  'grant_trial',
  'has_referral',
  'has_resume',
  'http_status',
  'identity_provider',
  'invoice_id',
  'is_admin',
  'is_paid_user',
  'is_quarterly',
  'is_trial',
  'initial_utm_campaign',
  'initial_utm_content',
  'initial_utm_medium',
  'initial_utm_source',
  'initial_utm_term',
  'initial_acquisition_channel',
  'initial_referring_domain',
  'job_seeker_id',
  'job_id',
  'landing_page',
  'latency_ms',
  'llm_feature',
  'model',
  'msclkid',
  'ai_model',
  'onboarding_version',
  'onboarding_attempt_id',
  'entry_type',
  'parsed_sections_count',
  'oppref',
  'path',
  'payment_failed',
  'plan',
  'plan_name',
  'price_id',
  'product_id',
  'prompt_tokens',
  'prompt_version',
  'reason',
  'referrer',
  'referring_domain',
  'retry_count',
  'route',
  'route_pattern',
  'seo_page_type',
  'signup_method',
  'signup_version',
  'signup_source',
  'source',
  'status',
  'step_id',
  'step_index',
  'step_name',
  'step_number',
  'stripe_customer_id',
  'stripe_event_id',
  'stripe_event_type',
  'stripe_session_id',
  'subscription_id',
  'survey_id',
  'survey_name',
  'survey_response',
  'template_version',
  'total_steps',
  'total_tokens',
  'trigger_source',
  'type',
  'url',
  'user_group',
  'user_id',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
  'variant',
]);

const BLOCKED_KEY_PATTERNS = [
  /address/i,
  /answer/i,
  /blocked.*compan/i,
  /company.*block/i,
  /cover.*letter/i,
  /demographic/i,
  /email/i,
  /first.*name/i,
  /gender/i,
  /hispanic/i,
  /last.*name/i,
  /letter/i,
  /password/i,
  /phone/i,
  /^prompt$/i,
  /prompt_text/i,
  /race/i,
  /references?/i,
  /resume/i,
  /secret/i,
  /auth.*token/i,
  /access.*token/i,
  /id.*token/i,
  /jwt/i,
  /veteran/i,
];

const SENSITIVE_TEXT_PATTERNS = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
  /\+?\d[\d().\-\s]{7,}\d/g,
  /sk_(live|test)_[A-Za-z0-9]+/g,
  /pk_(live|test)_[A-Za-z0-9]+/g,
  /Bearer\s+[A-Za-z0-9._-]+/gi,
];

export function sanitizeAnalyticsProperties(properties: AnalyticsProperties = {}): AnalyticsProperties {
  const sanitized: AnalyticsProperties = {};

  Object.entries(properties || {}).forEach(([rawKey, rawValue]) => {
    if (rawValue === undefined || rawValue === null) return;

    const key = PROPERTY_ALIASES[rawKey] || rawKey;
    if (isBlockedKey(rawKey) && !PROPERTY_ALIASES[rawKey]) return;
    if (!ALLOWED_PROPERTIES.has(key)) return;

    if (key === '$set' || key === '$set_once') {
      sanitized[key] = sanitizeAnalyticsProperties(rawValue as AnalyticsProperties);
      return;
    }

    const value = sanitizeValue(rawValue);
    if (value !== undefined && value !== null) {
      sanitized[key] = value;
    }
  });

  return sanitized;
}

export function sanitizeError(error: unknown, context: AnalyticsProperties = {}): AnalyticsProperties {
  const anyError = error as any;
  const status = anyError?.statusCode || anyError?.status || anyError?.response?.status;
  const code = anyError?.code || anyError?.name;
  const message = anyError?.message ? redactSensitiveText(String(anyError.message)).slice(0, 180) : undefined;

  return sanitizeAnalyticsProperties({
    ...context,
    errorName: anyError?.name || 'Error',
    errorCode: code,
    errorMessage: message,
    httpStatus: status,
  });
}

export function getStripeWebhookProperties(event: any, extra: AnalyticsProperties = {}): AnalyticsProperties {
  const object = event?.data?.object || {};
  const metadata = object?.metadata || {};

  return sanitizeAnalyticsProperties({
    '$insert_id': event?.id,
    stripeEventId: event?.id,
    stripeEventType: event?.type,
    userId: metadata?.userId,
    jobSeekerId: metadata?.jobSeekerId,
    autoId: metadata?.autoId,
    autoDateCreated: metadata?.dateCreated,
    planName: metadata?.planName,
    type: metadata?.type,
    subscriptionId: object?.subscription || object?.id,
    stripeCustomerId: object?.customer,
    invoiceId: object?.id?.startsWith?.('in_') ? object?.id : undefined,
    ...extra,
  });
}

function sanitizeValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactSensitiveText(value).slice(0, 300);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, 20)
      .map((item) => sanitizeValue(item))
      .filter((item) => item !== undefined && item !== null);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'object') {
    return sanitizeAnalyticsProperties(value as AnalyticsProperties);
  }

  return undefined;
}

function isBlockedKey(key: string): boolean {
  return BLOCKED_KEY_PATTERNS.some((pattern) => pattern.test(key));
}

function redactSensitiveText(value: string): string {
  return SENSITIVE_TEXT_PATTERNS.reduce((current, pattern) => current.replace(pattern, '[redacted]'), value);
}
